import { apiFetch } from '@/lib/api/client';
import type { PartnerSheet, SheetLevel, SheetLinks } from '@/features/partner-sheet/types';

export type PublicPartnerSheet = {
  slug: string;
  level: SheetLevel;
  brand: string;
  status: 'prefilled' | 'link_created' | 'opened' | 'submitted' | 'published' | 'withdrawn';
  sheet: PartnerSheet;
  submitted_at: string | null;
  is_published: boolean;
  source: { domain: string; consulted_on: string | null };
  links: SheetLinks;
  today: string;
};

export type PublishedPartnerSheet = {
  slug: string;
  level: SheetLevel;
  brand: string;
  verified: true;
  verified_on: string | null;
  published_at: string | null;
  sheet: PartnerSheet;
};

export function fetchPartnerSheet(token: string) {
  return apiFetch(`/api/marketplaces/partner-sheets/${encodeURIComponent(token)}/`) as Promise<PublicPartnerSheet>;
}

// « Voir » depuis /ops : la même fiche, par un lien signé qui ne la marque pas « ouverte ».
export function fetchPartnerSheetPreview(value: string) {
  return apiFetch(`/api/marketplaces/partner-sheets/preview/${encodeURIComponent(value)}/`) as Promise<PublicPartnerSheet>;
}

export function submitPartnerSheet(token: string, sheet: PartnerSheet, confirmed: boolean) {
  return apiFetch(`/api/marketplaces/partner-sheets/${encodeURIComponent(token)}/submit/`, {
    method: 'POST',
    body: JSON.stringify({ sheet, confirmed }),
  }) as Promise<PublicPartnerSheet>;
}

export function uploadPartnerSheetLogo(token: string, file: Blob, fileName: string) {
  const body = new FormData();
  body.append('file', file, fileName);
  return apiFetch(`/api/marketplaces/partner-sheets/${encodeURIComponent(token)}/logo/`, {
    method: 'POST',
    body,
  }) as Promise<{ logo_url: string }>;
}

// Fiche vérifiée d'un partenaire ou d'un magasin, pour l'écran de l'app (404 sans fiche en ligne).
export function fetchPublishedPartnerSheet(query: { partner: string } | { place: string }) {
  const params = new URLSearchParams(query as Record<string, string>);
  return apiFetch(`/api/marketplaces/partner-sheets/published/?${params.toString()}`) as Promise<PublishedPartnerSheet>;
}

// La fiche de la feuille « + d'infos » de l'app : la fiche en ligne si l'acteur l'a validée,
// sinon une fiche tirée de ce que la base sait déjà (catalogue partenaires, annuaire ADEME).
export type DisplayedPartnerSheet = {
  // published : validée par l'acteur (« Vérifié ») ; prefill : préremplie par Liberio ;
  // catalog / ademe : tirée de la base.
  origin: 'published' | 'prefill' | 'catalog' | 'ademe';
  verified: boolean;
  verified_on: string | null;
  confirmed_by: string;
  // Site officiel lu pour préremplir la fiche, et date de lecture.
  source_domain: string;
  consulted_on: string | null;
  sheet: PartnerSheet;
  links: SheetLinks;
};

export function fetchDisplayedPartnerSheet(query: { partner: string } | { place: string; route_kind?: string }) {
  const params = new URLSearchParams(
    Object.entries(query).filter(([, value]) => Boolean(value)) as Array<[string, string]>,
  );
  return apiFetch(`/api/marketplaces/partner-sheets/display/?${params.toString()}`) as Promise<DisplayedPartnerSheet>;
}
